import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { User, Mail, Phone, Edit2, Save, X, AlertCircle, ShieldOff, Clock } from 'lucide-react'
import { cn, formatCountdown } from '../../lib/utils'
import { authApi } from '../../services/api'
import { useAuthStore } from '../../stores/authStore'

interface UserProfile {
  id: number
  name: string
  email: string
  phone: string
  avatar?: string | null
  /** Rôles renvoyés par l'API sous forme de tableau (un utilisateur peut en avoir plusieurs). */
  roles?: string[]
  is_active: boolean
  wallet_balance: number
  rating: number
}

export default function ProfilePage() {
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<Partial<UserProfile>>({})
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Libellés des rôles fournis par le backend (/me/permissions → roles avec
  // display_name). On ne duplique AUCUNE liste de rôles côté frontend.
  const currentUser = useAuthStore((s) => s.user)
  // Session : échéance du jeton (fournie par le backend) et déconnexion globale.
  const tokenExpiresAt = useAuthStore((s) => s.tokenExpiresAt)
  const logoutAllSessions = useAuthStore((s) => s.logoutAllSessions)
  const roleLabel = (roles?: string[]): string =>
    (roles ?? [])
      .map((code) => {
        const meta = (currentUser?.roles ?? []).find(
          (r) => typeof r !== 'string' && r.name === code
        )
        return meta && typeof meta !== 'string' ? meta.display_name : code
      })
      .join(', ')

  // Récupérer le profil
  const { data: profileResponse, isLoading, isError, refetch } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      try {
        const response = await authApi.profile()
        return response.data
      } catch (err: any) {
        console.error('Profile fetch error:', err?.response?.data || err?.message || err)
        throw err
      }
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })

  const profile = (profileResponse?.data || profileResponse?.user || profileResponse) as UserProfile | undefined

  // Mutation pour mettre à jour le profil
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<UserProfile>) => {
      const updateData: Record<string, any> = {}
      
      // Mapper les noms de champs pour le backend
      if (data.name) updateData.name = data.name
      if (data.email) updateData.email = data.email
      if (data.avatar !== undefined) updateData.avatar = data.avatar || null

      console.log('Sending update data:', updateData)
      const response = await authApi.updateProfile(updateData)
      console.log('Update response:', response.data)
      return response.data
    },
    onSuccess: () => {
      setSuccess('Profil mis à jour avec succès!')
      setIsEditing(false)
      setFormData({})
      setTimeout(() => setSuccess(null), 3000)
      refetch()
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Erreur lors de la mise à jour du profil'
      setError(message)
      console.error('Profile update error:', error?.response?.data || error)
      setTimeout(() => setError(null), 4000)
    },
  })

  const handleStartEdit = () => {
    if (profile) {
      setFormData({
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
        avatar: profile.avatar || '',
      })
      setIsEditing(true)
      setError(null)
      setSuccess(null)
    }
  }

  const handleSave = () => {
    if (!formData.name?.trim()) {
      setError('Le nom est requis')
      return
    }
    if (!formData.email?.trim()) {
      setError('L\'email est requis')
      return
    }
    updateMutation.mutate(formData)
  }

  const handleCancel = () => {
    setIsEditing(false)
    setFormData({})
    setError(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (isError || !profile) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h3 className="font-semibold text-gray-900 mb-1">Erreur</h3>
        <p className="text-sm text-gray-500 mb-4">Impossible de charger le profil</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-amber-400 text-gray-900 rounded-xl text-sm font-semibold hover:bg-amber-500 transition-colors"
        >
          Réessayer
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <User className="w-7 h-7 text-amber-500" />
            Mon Profil
          </h1>
          <p className="text-sm text-gray-500 mt-1">Gérez vos informations personnelles</p>
        </div>
        {!isEditing && (
          <button
            onClick={handleStartEdit}
            className="flex items-center gap-2 px-4 py-2 bg-amber-400 text-gray-900 rounded-xl text-sm font-semibold hover:bg-amber-500 transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            Modifier
          </button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          {success}
        </div>
      )}

      {/* Profile Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-4">
          {/* Avatar */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Photo de profil</h2>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <span className="font-bold text-amber-700 text-lg">
                  {profile.name?.slice(0, 2).toUpperCase() || 'AD'}
                </span>
              </div>
              {isEditing && (
                <div className="flex-1">
                  <input
                    type="text"
                    value={formData.avatar ?? ''}
                    onChange={e => setFormData({ ...formData, avatar: e.target.value })}
                    placeholder="URL de l'avatar (optionnel)"
                    title="URL de l'avatar"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                  <p className="text-xs text-gray-500 mt-1">Entrez l'URL de votre image de profil</p>
                </div>
              )}
            </div>
          </div>

          {/* Personal Info */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Informations personnelles</h2>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Nom complet</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.name ?? ''}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nom complet"
                    title="Nom complet"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                ) : (
                  <p className="text-gray-900 font-medium">{profile.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Email</label>
                {isEditing ? (
                  <input
                    type="email"
                    value={formData.email ?? ''}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Email"
                    title="Email"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                  />
                ) : (
                  <p className="text-gray-900 font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    {profile.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm text-gray-600 font-medium mb-1">Téléphone</label>
                <p className="text-gray-900 font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400" />
                  {profile.phone}
                </p>
                {!isEditing && (
                  <p className="text-xs text-gray-500 mt-1">Non modifiable</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-xl text-sm font-semibold hover:bg-green-200 disabled:opacity-50 transition-colors"
              >
                <Save className="w-4 h-4" />
                {updateMutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <button
                onClick={handleCancel}
                disabled={updateMutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-semibold hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Annuler
              </button>
            </div>
          )}
        </div>

        {/* Sidebar - Account Info */}
        <div className="space-y-4">
          {/* Role & Status */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Informations du compte</h2>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Rôle</p>
                {/* Rôles sous forme de tableau : libellés joints (plusieurs rôles possibles) */}
                <p className="text-gray-900 font-medium">{roleLabel(profile.roles) || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Statut</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={cn(
                    'w-2 h-2 rounded-full',
                    profile.is_active ? 'bg-green-500' : 'bg-gray-300'
                  )} />
                  <span className="text-gray-900 font-medium">
                    {profile.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sécurité : le jeton de session a une durée de vie limitée (7 jours
              par défaut) et est renouvelé automatiquement avant son échéance. */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ShieldOff className="w-4 h-4 text-amber-500" />
              Sécurité de la session
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Expiration du jeton
                </span>
                <span className="font-medium text-gray-900">
                  {tokenExpiresAt ? formatCountdown(tokenExpiresAt) : '—'}
                </span>
              </div>
              <p className="text-xs text-gray-400">
                Le jeton est renouvelé automatiquement avant son échéance. En cas de
                doute sur la sécurité du compte, fermez toutes les sessions : chaque
                appareil devra se reconnecter.
              </p>
              <button
                onClick={() => {
                  if (confirm('Déconnecter toutes les sessions, y compris sur vos autres appareils ?')) {
                    void logoutAllSessions()
                  }
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 text-red-600 rounded-xl text-sm font-medium hover:bg-red-50 transition-colors"
              >
                <ShieldOff className="w-4 h-4" />
                Déconnecter toutes les sessions
              </button>
            </div>
          </div>

          {/* Stats : le bloc chauffeur s'affiche si « driver » figure parmi les rôles */}
          {profile.roles?.includes('driver') && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Statistiques</h2>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Évaluation</p>
                <p className="text-lg font-bold text-amber-500">
                  {profile.rating.toFixed(1)} ⭐
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Solde portefeuille</p>
                <p className="text-lg font-bold text-gray-900">
                  {profile.wallet_balance.toLocaleString()} FCFA
                </p>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  )
}
